import { LOCALE_LABEL, LOCALES, type Locale } from '@29100/shared';
import { Dropdown, DropdownItem, Icon } from './ui';
import { useI18n } from '../i18n';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { cn } from '../lib/cn';

/**
 * Selector de idioma.
 *
 * Guarda en dos sitios y no es redundancia: `localStorage` hace que el cambio
 * sea instantáneo y funcione sin sesión —quien aún no se ha registrado también
 * necesita leer la página—, y el perfil hace que la elección viaje con la
 * cuenta a otro dispositivo. Si falla el guardado en el perfil no se deshace
 * el cambio local: el idioma ya cambió delante del usuario y revertirlo sería
 * peor que perder la sincronización.
 */
export function LanguageSwitcher({ compact }: { compact?: boolean }) {
  const { locale, setLocale, t } = useI18n();
  const { session } = useSession();

  async function elegir(siguiente: Locale) {
    setLocale(siguiente);

    if (session) {
      await api('/api/users/me', {
        method: 'PATCH',
        body: { locale: siguiente },
      }).catch(() => undefined);
    }
  }

  return (
    <Dropdown
      label={t('common.language')}
      trigger={() => (
        <span
          className={cn(
            'inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-muted transition hover:text-content',
            compact && 'px-2',
          )}
        >
          <Icon name="globe" className="h-4 w-4" />
          {compact ? locale.toUpperCase() : LOCALE_LABEL[locale]}
        </span>
      )}
    >
      {({ close }) => (
        <>
          {LOCALES.map((code) => (
            <DropdownItem
              key={code}
              onClick={() => {
                void elegir(code);
                close();
              }}
            >
              <span className="flex-1">{LOCALE_LABEL[code]}</span>
              {code === locale ? (
                <>
                  <span aria-hidden="true">✓</span>
                  {/* El ✓ no se lee: sin esto, las dos opciones suenan igual. */}
                  <span className="sr-only">{t('common.current')}</span>
                </>
              ) : null}
            </DropdownItem>
          ))}
        </>
      )}
    </Dropdown>
  );
}
